import type { StyleProp, ViewStyle } from 'react-native';
import type { FormDataFieldSpec } from '../../../components/forms/dataFieldSpec';
import { OPPORTUNITY_FIELDS } from '../steps/opportunity.fields';

/** Site assessment fields on Opportunity → Project Details (registry order). */
export const OPPORTUNITY_SCOPE_ASSESSMENT_DATA_FIELDS = [
  'oppSiteAssessmentRequired',
  'oppPreferredAssessmentDate',
  'oppSiteAddress',
] as const;

export type OpportunityScopeAssessmentDataField =
  (typeof OPPORTUNITY_SCOPE_ASSESSMENT_DATA_FIELDS)[number];

function registryLabel(dataField: OpportunityScopeAssessmentDataField): string {
  const row = OPPORTUNITY_FIELDS.find((f) => f.dataField === dataField);
  if (!row) {
    throw new Error(`Missing opportunity registry row for ${dataField}`);
  }
  return row.label;
}

/**
 * Project Details scope assessment inputs (assessment → date → site address).
 * Labels come from {@link OPPORTUNITY_FIELDS}; UI shape is step-specific.
 */
export function buildOpportunityScopeAssessmentFieldSpecs(p: {
  preferredDateContainerStyle: StyleProp<ViewStyle>;
  siteAddressContainerStyle: StyleProp<ViewStyle>;
}): FormDataFieldSpec[] {
  return [
    {
      kind: 'yesNo',
      permissionFieldId: 'oppSiteAssessmentRequired',
      fieldKey: 'oppSiteAssessmentRequired',
      label: registryLabel('oppSiteAssessmentRequired'),
      required: false,
      testID: 'form-yes-no-oppSiteAssessmentRequired',
      inline: true,
    },
    {
      kind: 'text',
      permissionFieldId: 'oppPreferredAssessmentDate',
      fieldKey: 'oppPreferredAssessmentDate',
      label: registryLabel('oppPreferredAssessmentDate'),
      placeholder: 'e.g., 04/15/2026, Q2 2026, or TBD',
      testID: 'form-input-oppPreferredAssessmentDate',
      containerStyle: p.preferredDateContainerStyle,
    },
    {
      kind: 'text',
      permissionFieldId: 'oppSiteAddress',
      fieldKey: 'oppSiteAddress',
      label: registryLabel('oppSiteAddress'),
      placeholder: 'Enter the full street address for this site walk',
      multiline: true,
      numberOfLines: 3,
      webMultilineAutoSize: true,
      webMultilineMinHeight: 100,
      webMultilineMaxHeight: 100,
      maxLength: 10000,
      helpInfoKey: 'oppSiteAddress',
      testID: 'form-input-oppSiteAddress',
      containerStyle: p.siteAddressContainerStyle,
    },
  ];
}

/** Web testIDs for scope assessment fields in registry order. */
export function getOpportunityScopeAssessmentFormTestIdsInOrder(): string[] {
  const specs = buildOpportunityScopeAssessmentFieldSpecs({
    preferredDateContainerStyle: {},
    siteAddressContainerStyle: {},
  });
  const out: string[] = [];
  for (const spec of specs) {
    if ('testID' in spec && spec.testID) {
      out.push(spec.testID);
    }
  }
  return out;
}
